import React, { ChangeEvent, FC, useState } from 'react'
import { H3 } from 'components/base/typography/headings'
import useTypedSelector from 'interfaces/useTypedSelector'
import { ListButton } from 'components/base/lists/lists'
import styled from 'styled-components'
import { useActiveCompany } from 'hooks/useActiveCompany/useActiveCompany'
import { useCompanySearch } from 'hooks/useCompanySearch/useCompanySearch'
import { useDispatch } from 'react-redux'
import { setActiveModal } from 'reducers/modalReducer'
import { P } from 'components/base/typography/misc'
import { StrippedButton } from 'components/base/buttons/buttons'
import { setActiveProductList } from '../../../reducers/productListReducer'
import { Input } from 'components/base/forms/input'
import { StorefrontCompany } from '@local-types/user/storefrontUser'

const StyledWrapper = styled.div`
	width: 36.2rem;
	max-width: 90%;
	margin: 0 auto;
	padding-top: 3.5rem;
	background: #fff;
	box-shadow: 0px 10px 34px rgba(0, 0, 0, 0.1);
	max-height: 80vh;
	overflow-y: auto;
`

const InputWrapper = styled.div`
	padding: 0 1.5rem 1.5rem 1.5rem;
`

const InputLabel = styled.label`
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border-width: 0;
`

const LoadMoreWrapper = styled.div`
	display: flex;
	justify-content: center;
	padding: 1.5rem 0 2rem;
`

const CompanyPicker: FC<{}> = () => {
	const dispatch = useDispatch()
	const { userInfo, userInfoState } = useTypedSelector(({ auth }) => auth)
	const { activeRelation, setRelation, setSalesPersonCompany, isSalesPerson } = useActiveCompany()
	const companySearch = useCompanySearch(isSalesPerson)

	const [search, setSearch] = useState('')
	const handleSearch = (e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)

	const closeAfterSelect = () => {
		dispatch(setActiveProductList(null))
		dispatch(setActiveModal(''))
	}

	const handleRelationClick = (id: string) => {
		setRelation(id)
		closeAfterSelect()
	}

	const handleSalesPersonSelect = (company: StorefrontCompany) => {
		setSalesPersonCompany(company)
		closeAfterSelect()
	}

	if (isSalesPerson) {
		return (
			<StyledWrapper>
				<H3 mb="3rem" ml="3.5rem" mr="3.5rem">
					Veldu fyrirtæki
				</H3>
				<InputWrapper>
					<InputLabel htmlFor="company-search">Leita</InputLabel>
					<Input
						id="company-search"
						placeholder="Leita eftir nafni eða kennitölu..."
						value={companySearch.search}
						onChange={e => companySearch.setSearch(e.target.value)}
					/>
				</InputWrapper>
				{companySearch.companies.map(company => (
					<ListButton
						key={company.id}
						active={activeRelation?.company.id === company.id}
						onClick={handleSalesPersonSelect.bind(null, company)}>
						{company.name}
					</ListButton>
				))}
				{companySearch.loading && (
					<P ml="3.5rem" mr="3.5rem" pb="2rem">
						Hleð...
					</P>
				)}
				{!companySearch.loading && companySearch.companies.length === 0 && (
					<P ml="3.5rem" mr="3.5rem" pb="5rem">
						Engin fyrirtæki fundust
					</P>
				)}
				{companySearch.hasMore && (
					<LoadMoreWrapper>
						<StrippedButton primary onClick={companySearch.loadMore} disabled={companySearch.loading}>
							Sækja fleiri
						</StrippedButton>
					</LoadMoreWrapper>
				)}
			</StyledWrapper>
		)
	}

	const filteredRelations =
		search.length > 0
			? (userInfo?.companyUsers ?? []).filter(
					relation =>
						relation.company.name.toLowerCase().includes(search.toLowerCase()) ||
						relation.company.ssn.toLowerCase().includes(search.toLowerCase())
			  )
			: userInfo?.companyUsers ?? []

	return (
		<StyledWrapper>
			<H3 mb="3rem" ml="3.5rem" mr="3.5rem">
				Veldu aðra kennitölu
			</H3>
			{userInfo?.companyUsers && userInfo.companyUsers.length > 2 && (
				<InputWrapper>
					<InputLabel htmlFor="company-search">Leita</InputLabel>
					<Input id="company-search" placeholder="Leita..." value={search} onChange={handleSearch} />
				</InputWrapper>
			)}
			{filteredRelations.map(relation => (
				<ListButton
					key={relation.id}
					active={activeRelation?.id === relation.id}
					onClick={handleRelationClick.bind(null, relation.id)}>
					{relation.company.name}
				</ListButton>
			))}
			{userInfoState === 'fulfilled' && userInfo && userInfo.companyUsers.length === 0 && (
				<P ml="3.5rem" mr="3.5rem" pb="5rem">
					Þú virðist ekki hafa aðgang að neinum kennitölum ennþá
				</P>
			)}
		</StyledWrapper>
	)
}

export default CompanyPicker
